import { EpocusLogomark } from "$components/epocus-logomark";
import { useAuth0 } from "@auth0/auth0-react";
import { Box, ButtonBase, styled, Typography } from "@material-ui/core";
import { Link, useLocation } from "react-router-dom";
import { getCurrentTab, linkToEpocusSettings } from "../links";
import { EpocusAvatar } from "./epocus-avatar";
import {
  isNavGroup,
  type NavBadge,
  type NavBadgeTone,
  type NavItemDef,
  useVisibleNavEntries,
} from "./epocus-nav-items";
import {
  EPOCUS_AMBER,
  EPOCUS_BORDER,
  EPOCUS_INACTIVE,
  EPOCUS_PRIMARY_HOVER_BG,
  EPOCUS_RADIUS_NAV,
  EPOCUS_RADIUS_PILL,
  EPOCUS_RED,
  EPOCUS_SELECTED_BG,
  EPOCUS_TEAL_DARK,
  EPOCUS_TEXT_SECONDARY,
  EPOCUS_WHITE,
  rem,
} from "./epocus-palette";
import { epocusTheme } from "./epocus-theme-provider";
import { useEpocusRole } from "./rbac/epocus-claim";
import { roleLabel } from "./rbac/role-label";
import { WorkModeSwitch } from "./work-mode-switch";

const SIDEBAR_WIDTH = 250;

const Root = styled(Box)({
  // Hidden on phone, visible on tablet+
  display: "none",
  [epocusTheme.breakpoints.up("sm")]: {
    display: "flex",
    flexDirection: "column",
    width: SIDEBAR_WIDTH,
    flexShrink: 0,
    borderRight: `1px solid ${EPOCUS_BORDER}`,
    backgroundColor: EPOCUS_WHITE,
  },
});

const LogoArea = styled(Link)({
  display: "flex",
  alignItems: "center",
  gap: 12,
  padding: "22px 22px 8px",
  textDecoration: "none",
  color: "inherit",
  cursor: "pointer",
});

const BrandText = styled(Box)({
  display: "flex",
  flexDirection: "column",
});

const BrandTitle = styled(Typography)({
  fontWeight: 700,
  fontSize: rem(21),
  lineHeight: 1.2,
});

const NavList = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: 3,
  padding: "14px 16px",
  flex: 1,
});

const NavItem = styled(ButtonBase)({
  display: "flex",
  alignItems: "center",
  gap: 12,
  padding: "12px 13px",
  borderRadius: EPOCUS_RADIUS_NAV,
  width: "100%",
  minHeight: 44,
  color: EPOCUS_TEXT_SECONDARY,
  fontSize: rem(14.5),
  fontWeight: 500,
  textDecoration: "none",
  transition: "background-color 150ms, color 150ms",
  "&:hover": {
    backgroundColor: EPOCUS_PRIMARY_HOVER_BG,
  },
  "&.active": {
    color: EPOCUS_TEAL_DARK,
    backgroundColor: EPOCUS_SELECTED_BG,
    fontWeight: 600,
  },
});

const BADGE_TONE_COLOUR: Record<NavBadgeTone, string> = {
  red: EPOCUS_RED,
  amber: EPOCUS_AMBER,
};

const NavBadgePill = styled("span")({
  marginLeft: "auto",
  display: "grid",
  placeItems: "center",
  minWidth: 20,
  height: 20,
  padding: "0 6px",
  borderRadius: EPOCUS_RADIUS_PILL,
  color: EPOCUS_WHITE,
  fontSize: rem(11),
  fontWeight: 700,
  lineHeight: 1,
});

const NavGroup = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: 2,
  marginTop: 8,
});

const NavGroupHeading = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: 10,
  padding: "6px 12px",
  color: EPOCUS_TEXT_SECONDARY,
  fontSize: rem(12),
  fontWeight: 700,
  letterSpacing: "0.06em",
  textTransform: "uppercase" as const,
});

const NavGroupItems = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: 2,
  paddingLeft: 14,
  marginLeft: 10,
  borderLeft: `1px solid ${EPOCUS_BORDER}`,
});

const UserSection = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: 4,
  paddingRight: 14,
  borderTop: `1px solid ${EPOCUS_BORDER}`,
});

const UserArea = styled(ButtonBase)({
  display: "flex",
  alignItems: "center",
  gap: 10,
  flex: 1,
  minWidth: 0,
  padding: "16px 0 16px 22px",
  textAlign: "left",
  textDecoration: "none",
  color: "inherit",
  "&:hover": {
    backgroundColor: EPOCUS_PRIMARY_HOVER_BG,
  },
});

const UserText = styled(Box)({
  minWidth: 0,
  "& > *": {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
});

const UserName = styled(Typography)({
  fontSize: rem(13.5),
  fontWeight: 600,
  color: EPOCUS_TEAL_DARK,
  lineHeight: 1.3,
});

const UserRole = styled(Typography)({
  fontSize: rem(11.5),
  color: EPOCUS_INACTIVE,
  lineHeight: 1.3,
});

const NavLink: FC<{
  item: NavItemDef;
  active: boolean;
  badgeCount: number;
}> = ({ item: { label, path, icon: Icon, badge }, active, badgeCount }) => (
  <NavItem
    // @ts-expect-error -- MUI v4 styled() drops component/to from ButtonBase type
    component={Link}
    to={path()}
    className={active ? "active" : undefined}
  >
    <Icon />
    {label}
    {badge !== null && badgeCount > 0 && (
      <NavBadgePill style={{ backgroundColor: BADGE_TONE_COLOUR[badge.tone] }}>
        {badgeCount}
      </NavBadgePill>
    )}
  </NavItem>
);

export const EpocusSidebar: FC<{
  reviewCount?: number;
  referralCount?: number;
  incompleteCount?: number;
}> = ({ reviewCount = 0, referralCount = 0, incompleteCount = 0 }) => {
  const { user } = useAuth0();
  const { pathname } = useLocation();
  const currentTab = getCurrentTab(pathname);
  const entries = useVisibleNavEntries();
  const role = useEpocusRole();

  const badgeCounts: Record<NavBadge["countKey"], number> = {
    review: reviewCount,
    referral: referralCount,
    incomplete: incompleteCount,
  };

  /*
   * Program oversight has no Home item, so a fixed /epocus/home here would
   * bounce an admin out of their own board. The first visible entry is that
   * role's landing by construction.
   */
  const firstEntry = entries[0];
  const landingPath =
    firstEntry === undefined
      ? linkToEpocusSettings()
      : isNavGroup(firstEntry)
        ? (firstEntry.items[0]?.path() ?? linkToEpocusSettings())
        : firstEntry.path();

  const renderItem = (item: NavItemDef) => (
    <NavLink
      key={item.path()}
      item={item}
      active={currentTab === item.path()}
      badgeCount={item.badge ? badgeCounts[item.badge.countKey] : 0}
    />
  );

  return (
    <Root>
      <LogoArea to={landingPath} aria-label="Go to home">
        <EpocusLogomark size={40} />
        <BrandText>
          <BrandTitle>ePOCUS</BrandTitle>
        </BrandText>
      </LogoArea>

      <NavList>
        {entries.map((entry) =>
          isNavGroup(entry) ? (
            <NavGroup key={entry.group}>
              <NavGroupHeading>
                <entry.icon />
                {entry.group}
              </NavGroupHeading>
              <NavGroupItems>{entry.items.map(renderItem)}</NavGroupItems>
            </NavGroup>
          ) : (
            renderItem(entry)
          ),
        )}
      </NavList>

      <UserSection>
        <UserArea
          // @ts-expect-error -- MUI v4 styled() drops component/to from ButtonBase type
          component={Link}
          to={linkToEpocusSettings()}
        >
          <EpocusAvatar name={user?.name} />
          <UserText>
            <UserName>{user?.name ?? "Unknown"}</UserName>
            {role !== null && <UserRole>{roleLabel(role)}</UserRole>}
          </UserText>
        </UserArea>
        <WorkModeSwitch opensUpward />
      </UserSection>
    </Root>
  );
};
